class ScoreggiaInformationOverlays {
  constructor(root) {
    this.root = root;
    this.panels = new Map(
      [...root.querySelectorAll('[data-scoreggia-overlay]')].map((panel) => [panel.id, panel]),
    );
    this.activePanel = null;
    this.returnFocusTo = null;
    this.inertTargets = [];
    this.scrollPosition = 0;
    this.boundClick = this.onClick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundHistoryChange = this.onHistoryChange.bind(this);

    this.panels.forEach((panel) => {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('tabindex', '-1');
      const titleId = panel.dataset.scoreggiaOverlayTitle;
      if (titleId) panel.setAttribute('aria-labelledby', titleId);
    });

    root.addEventListener('click', this.boundClick);
    document.addEventListener('keydown', this.boundKeydown);
    window.addEventListener('popstate', this.boundHistoryChange);

    const postedPanel = root.querySelector('[data-scoreggia-open-on-load]')?.closest('[data-scoreggia-overlay]');
    const hashPanel = this.panels.get(decodeURIComponent(window.location.hash.slice(1)));
    if (postedPanel || hashPanel) this.open(postedPanel || hashPanel, null, false);
  }

  destroy() {
    if (this.activePanel) this.closeImmediately(false);
    this.root.removeEventListener('click', this.boundClick);
    document.removeEventListener('keydown', this.boundKeydown);
    window.removeEventListener('popstate', this.boundHistoryChange);
  }

  onClick(event) {
    const searchTrigger = event.target.closest('[data-scoreggia-search-trigger]');
    if (searchTrigger) {
      const nativeSearch = document.querySelector('details-modal.header__search summary');
      if (nativeSearch) {
        event.preventDefault();
        nativeSearch.click();
      }
      return;
    }

    const openTrigger = event.target.closest('[data-scoreggia-overlay-open]');
    if (openTrigger) {
      const panel = this.panels.get(openTrigger.dataset.scoreggiaOverlayOpen);
      if (panel) {
        event.preventDefault();
        this.open(panel, openTrigger, true);
      }
      return;
    }

    const closeTrigger = event.target.closest('[data-scoreggia-overlay-close]');
    if (closeTrigger && this.activePanel) {
      event.preventDefault();
      this.requestClose();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.activePanel) {
      event.preventDefault();
      this.requestClose();
    }
  }

  onHistoryChange() {
    const hashPanel = this.panels.get(decodeURIComponent(window.location.hash.slice(1)));
    if (hashPanel) {
      this.open(hashPanel, this.returnFocusTo, false);
    } else if (this.activePanel) {
      this.closeImmediately(true);
    }
  }

  open(panel, trigger, updateHistory) {
    if (this.activePanel === panel) return;
    if (this.activePanel) this.closeImmediately(false);

    this.activePanel = panel;
    this.returnFocusTo = trigger || this.returnFocusTo || document.activeElement;
    this.scrollPosition = window.scrollY;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('scoreggia-overlay-open');

    this.inertTargets = [
      ...document.querySelectorAll(
        '.shopify-section-group-header-group, #MainContent, footer.footer, .scoreggia-information__nav',
      ),
    ].filter((element) => !element.contains(panel));
    this.inertTargets.forEach((element) => element.setAttribute('inert', ''));

    const initialFocus = panel.querySelector('[data-scoreggia-overlay-close]') || panel;
    if (typeof trapFocus === 'function') {
      trapFocus(panel, initialFocus);
    } else {
      initialFocus.focus();
    }

    window.requestAnimationFrame(() => initialFocus.focus({ preventScroll: true }));
    window.setTimeout(() => initialFocus.focus({ preventScroll: true }), 60);

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.hash = panel.id;
      window.history.pushState({ scoreggiaOverlay: panel.id }, '', url);
    }
  }

  requestClose() {
    if (!this.activePanel) return;
    if (window.history.state?.scoreggiaOverlay === this.activePanel.id) {
      window.history.back();
    } else {
      this.closeImmediately(true);
    }
  }

  closeImmediately(restoreFocus) {
    if (!this.activePanel) return;
    const focusTarget = restoreFocus ? this.returnFocusTo : null;
    this.activePanel.classList.remove('is-open');
    this.activePanel.setAttribute('aria-hidden', 'true');
    this.inertTargets.forEach((element) => element.removeAttribute('inert'));
    this.inertTargets = [];
    document.documentElement.classList.remove('scoreggia-overlay-open');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusTarget);
    } else if (focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }

    window.scrollTo({ top: this.scrollPosition, behavior: 'auto' });
    this.activePanel = null;
    if (restoreFocus) this.returnFocusTo = null;
  }
}

const scoreggiaInformationInstances = new WeakMap();

function initializeScoreggiaInformation(container) {
  const roots = [];
  if (container.matches?.('[data-scoreggia-information]')) roots.push(container);
  roots.push(...container.querySelectorAll?.('[data-scoreggia-information]') || []);

  roots.forEach((root) => {
    if (!scoreggiaInformationInstances.has(root)) {
      scoreggiaInformationInstances.set(root, new ScoreggiaInformationOverlays(root));
    }
  });
}

document.addEventListener('DOMContentLoaded', () => initializeScoreggiaInformation(document));

document.addEventListener('shopify:section:load', (event) => {
  initializeScoreggiaInformation(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.matches?.('[data-scoreggia-information]')
    ? event.target
    : event.target.querySelector?.('[data-scoreggia-information]');
  const instance = root && scoreggiaInformationInstances.get(root);
  if (instance) {
    instance.destroy();
    scoreggiaInformationInstances.delete(root);
  }
});
